import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch {
        // keep polling briefly
      }
      attempts += 1;
      if (attempts < 8) timer = setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="bg-cream pt-16 min-h-screen">
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        {status === "checking" && (
          <>
            <Loader2 size={36} className="mx-auto text-coral animate-spin" />
            <h1 className="mt-8 font-serif text-4xl text-navy">Confirming your payment…</h1>
            <p className="mt-4 text-sm text-charcoal/70">One moment while we verify with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={40} className="mx-auto text-coral" />
            <h1 className="mt-8 font-serif text-4xl sm:text-5xl text-navy" data-testid="payment-confirmed-heading">
              Thank you. Your guide is on its way.
            </h1>
            <p className="mt-6 text-base leading-relaxed text-charcoal/70">
              Your payment is confirmed. A download link will arrive at the email address you used at checkout.
            </p>
            <Link to="/store" data-testid="payment-back-store" className="mt-10 inline-block bg-navy hover:bg-navy-surface text-cream-surface font-medium px-8 py-3.5 rounded-full transition-colors duration-300">
              Back to the store
            </Link>
          </>
        )}
        {(status === "pending" || status === "error") && (
          <>
            <Loader2 size={36} className="mx-auto text-coral" />
            <h1 className="mt-8 font-serif text-4xl text-navy">We're still confirming…</h1>
            <p className="mt-4 text-sm text-charcoal/70">
              Payment confirmations occasionally take a minute. If you were charged, your download link will arrive by email — no need to pay again.
            </p>
            <Link to="/store" data-testid="payment-back-store-pending" className="mt-10 inline-block border border-navy text-navy font-medium px-8 py-3.5 rounded-full transition-colors duration-300 hover:bg-navy hover:text-cream-surface">
              Back to the store
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page" className="bg-cream pt-16 min-h-screen">
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <XCircle size={40} className="mx-auto text-charcoal/40" />
        <h1 className="mt-8 font-serif text-4xl sm:text-5xl text-navy">No charge was made.</h1>
        <p className="mt-6 text-base leading-relaxed text-charcoal/70">
          The checkout was cancelled. The guides will be waiting whenever you are ready.
        </p>
        <Link to="/store" data-testid="cancel-back-store" className="mt-10 inline-block bg-coral hover:bg-coral-dark text-white font-medium px-8 py-3.5 rounded-full transition-colors duration-300">
          Return to the store
        </Link>
      </div>
    </div>
  );
}
